import { useCallback, useMemo } from "react";
import { createDefaultOrdersAnalyticsFilters } from "@/features/orders-analytics/hooks/use-orders-analytics-filters";
import { timezone } from "@/features/orders-analytics/utils/timezone";
import type { OrdersAnalyticsFilters } from "@/features/orders-analytics/types/orders-analytics.types";
import type { DailyQueryPatch, DailyQueryState } from "@/features/daily/hooks/use-daily-query-state";

/**
 * D105: Daily reuses the app's own `FiltersBar` (workspace + range + refresh). This
 * adapter is the only place that translates between that component's
 * `OrdersAnalyticsFilters` and Daily's URL (`scope`, `window`, `from`, `to`).
 *
 * - «All clients» ⇄ no `scope` (the platform read); one client ⇄ `scope=<id>`,
 *   canonicalised exactly as the Daily parser does it (ledger L253).
 * - «Month to date» ⇄ `window=month_to_date`: a preset, so the server decides where
 *   the month currently ends (L354).
 * - «Last full month» and «Custom date» ⇄ `window=custom&from&to` in epoch ms.
 * - No `window` at all ⇄ the page's live default (Salud, Funnel, Diagnostic: today;
 *   Executive: month to date). A live "today" lights none of the three presets,
 *   because none of them is what is on screen.
 */
export type DailyLiveWindow = "today" | "month_to_date";

// Mirror of `canonicalClient` in backend/.../daily-query.schema.ts (itself a mirror of
// `canonicalWorkspaceId` in cloud-functions firestorePaths.ts): the writer indexes the
// canonical spelling, so a raw one would select nothing.
const canonicalClient = (workspaceId: string): string =>
    workspaceId.toLowerCase() === "enviadinero" ? "enviaDinero" : workspaceId;

const readBound = (raw: string | null): number | null => {
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
};

const isLastFullMonth = (from: number, to: number, now: number): boolean => {
    const range = timezone.getTimeZoneLastFullMonthRange(now);
    return range.from === from && range.to === to;
};

const PERIOD_TEXT: Partial<Record<OrdersAnalyticsFilters["comparisonPeriod"], string>> = {
    day: "Hoy",
    month_to_date: "Mes en curso",
    last_full_month: "Último mes completo",
    previous_range: "Rango personalizado",
};

export const dailyFiltersAdapter = {
    canonicalClient,

    /** The chip text for the range the bar is showing. */
    periodLabel: (filters: OrdersAnalyticsFilters): string => PERIOD_TEXT[filters.comparisonPeriod] ?? "Rango personalizado",

    /** What the bar should show for the current URL. */
    toFilters: (
        query: Pick<DailyQueryState, "scope" | "windowPreset" | "windowFrom" | "windowTo">,
        live: DailyLiveWindow,
        now: number,
    ): OrdersAnalyticsFilters => {
        const base = createDefaultOrdersAnalyticsFilters();
        const clients = query.scope.map(canonicalClient);
        const from = readBound(query.windowFrom);
        const to = readBound(query.windowTo);
        if (query.windowPreset === "custom" && from !== null && to !== null) {
            return {
                ...base,
                clients,
                dateFrom: from,
                dateTo: to,
                comparisonPeriod: isLastFullMonth(from, to, now) ? "last_full_month" : "previous_range",
            };
        }
        const preset = query.windowPreset ?? live;
        if (preset === "month_to_date") {
            return { ...base, clients, comparisonPeriod: "month_to_date", dateFrom: timezone.getTimeZoneMonthStartTimestamp(now), dateTo: now };
        }
        // Live "today": a period none of the bar's buttons stands for.
        return { ...base, clients, comparisonPeriod: "day", dateFrom: timezone.getTimeZoneDayStartTimestamp(now), dateTo: now };
    },

    /**
     * What the URL becomes when the bar changes. A new workspace or range is a new
     * question: the position inside the previous one (cursor, page) is dropped.
     */
    toPatch: (next: OrdersAnalyticsFilters): DailyQueryPatch => {
        const scope = Array.from(new Set(next.clients.map(canonicalClient)));
        const reset: DailyQueryPatch = { cursor: null, page: null };
        if (next.comparisonPeriod === "month_to_date") {
            return { ...reset, scope, windowPreset: "month_to_date", windowFrom: null, windowTo: null };
        }
        if (next.comparisonPeriod === "last_full_month" || next.comparisonPeriod === "previous_range") {
            return {
                ...reset,
                scope,
                windowPreset: "custom",
                windowFrom: String(next.dateFrom),
                windowTo: String(next.dateTo),
            };
        }
        return { ...reset, scope };
    },
};

export type DailyFiltersApi = {
    filters: OrdersAnalyticsFilters;
    onFiltersChange: (next: OrdersAnalyticsFilters) => void;
};

export const useDailyFilters = (
    query: DailyQueryState,
    set: (patch: DailyQueryPatch) => void,
    live: DailyLiveWindow,
    extraPatch?: (next: OrdersAnalyticsFilters) => DailyQueryPatch,
): DailyFiltersApi => {
    const scopeKey = query.scope.join(",");
    const { windowPreset, windowFrom, windowTo } = query;
    const filters = useMemo(
        () => dailyFiltersAdapter.toFilters(
            { scope: scopeKey === "" ? [] : scopeKey.split(","), windowPreset, windowFrom, windowTo },
            live,
            Date.now(),
        ),
        [scopeKey, windowPreset, windowFrom, windowTo, live],
    );
    const onFiltersChange = useCallback((next: OrdersAnalyticsFilters): void => {
        set({ ...dailyFiltersAdapter.toPatch(next), ...(extraPatch === undefined ? {} : extraPatch(next)) });
    }, [set, extraPatch]);
    return { filters, onFiltersChange };
};
